import { useState, useRef, useCallback, useEffect } from "react";
import {
  Image,
  Upload,
  X,
  FileImage,
  AlertCircle,
  Loader2,
  Camera,
  SwitchCamera,
} from "lucide-react";
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import {
  getPresignedUploadUrl,
  uploadImageToR2,
  saveImageMetadata,
  ImageData,
} from "../../api/processImages";
import {
  getPresignedUploadUrlForPrescription,
  uploadImageToR2 as uploadImageToR2Prescription,
  saveImageMetadataForPrescription,
} from "../../api/prescriptionImages";
import useRetryFetch from "../../hooks/useRetryFetch";
import { useTranslation } from "react-i18next";

// Allowed image types
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const ALLOWED_EXTENSIONS = ".jpg,.jpeg,.png,.webp,.gif";

// Default max size: 10MB
const DEFAULT_MAX_SIZE = 10 * 1024 * 1024;

interface ImageUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (imageData: ImageData) => void;
  context: "additionalInfo" | "pane" | "prologue" | "epilogue";
  prescriptionId?: string; // For author uploads (prescription context)
  processId?: string; // For user uploads (process context)
  activityId?: string; // Component/field ID
  maxSize?: number; // Override default limit
  existingImage?: ImageData; // For viewing existing image
  mode?: "upload" | "view"; // Determine initial state
}

export const ImageUploadDialog = ({
  open,
  onOpenChange,
  onComplete,
  context,
  prescriptionId,
  processId,
  activityId,
  maxSize = DEFAULT_MAX_SIZE,
  existingImage,
  mode = "upload",
}: ImageUploadDialogProps) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Camera state
  const [inputMode, setInputMode] = useState<"file" | "camera">("file");
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">(
    "environment",
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const retryFetch = useRetryFetch();

  // Start camera
  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      // Stop existing stream if any
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode },
        audio: false,
      });

      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera error:", err);
      setCameraError(
        t(
          "camera_access_denied",
          "Unable to access camera. Please check permissions.",
        ),
      );
    }
  }, [facingMode, cameraStream, t]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
  }, [cameraStream]);

  // Switch camera (front/back)
  const switchCamera = useCallback(() => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  }, []);

  // Capture photo from camera
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);

    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        // Create file from blob
        const file = new File([blob], `camera-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });

        // Stop camera after capture
        stopCamera();
        setInputMode("file");

        // Use existing file selection handler
        handleFileSelect(file);
      },
      "image/jpeg",
      0.9,
    );
  }, [stopCamera]);

  // Effect to start/stop camera when mode changes
  useEffect(() => {
    if (inputMode === "camera" && open) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [inputMode, open, facingMode]);

  // Effect to restart camera when facing mode changes
  useEffect(() => {
    if (inputMode === "camera" && open && cameraStream) {
      startCamera();
    }
  }, [facingMode]);

  // Format file size
  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Validate file
  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return t(
        "invalid_image_type",
        "Please select a valid image (JPEG, PNG, WebP, or GIF)",
      );
    }
    if (file.size > maxSize) {
      return (
        t("image_too_large", { size: formatSize(maxSize) }) ||
        `Image exceeds maximum size of ${formatSize(maxSize)}`
      );
    }
    return null;
  };

  // Handle file selection
  const handleFileSelect = useCallback(
    (file: File) => {
      setError(null);

      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setSelectedFile(file);

      // Get image dimensions
      const img = new window.Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
      };
      img.src = url;
    },
    [maxSize, t],
  );

  // Handle file input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Handle drag events
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Clear selection
  const clearSelection = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setImageDimensions(null);
    setError(null);
    setCameraError(null);
    setInputMode("file");
    stopCamera();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || !activityId) return;

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Determine if this is a prescription or process upload
      const isPrescription = context === "additionalInfo" && prescriptionId;

      // Step 1: Get presigned URL
      setUploadProgress(10);
      let presignedData;
      if (isPrescription) {
        presignedData = await getPresignedUploadUrlForPrescription(
          prescriptionId!,
          activityId,
          selectedFile.type,
          retryFetch,
        );
      } else if (processId) {
        presignedData = await getPresignedUploadUrl(
          processId,
          activityId,
          selectedFile.type,
          retryFetch,
        );
      } else {
        // Deferred upload - return local data for parent to handle
        const imageData: ImageData = {
          url: previewUrl!,
          key: "",
          mimeType: selectedFile.type,
          size: selectedFile.size,
          width: imageDimensions?.width,
          height: imageDimensions?.height,
          uploadedAt: new Date().toISOString(),
        };
        // Store file reference for later upload
        (imageData as any)._file = selectedFile;
        (imageData as any)._blobUrl = previewUrl;

        setUploading(false);
        onComplete(imageData);
        onOpenChange(false);
        return;
      }

      // Step 2: Upload to R2
      setUploadProgress(40);
      if (isPrescription) {
        await uploadImageToR2Prescription(
          presignedData.uploadUrl,
          selectedFile,
          selectedFile.type,
        );
      } else {
        await uploadImageToR2(
          presignedData.uploadUrl,
          selectedFile,
          selectedFile.type,
        );
      }

      // Step 3: Save metadata
      setUploadProgress(80);
      let savedImage;
      if (isPrescription) {
        const result = await saveImageMetadataForPrescription(
          prescriptionId!,
          {
            key: presignedData.key,
            activityId,
            size: selectedFile.size,
            mimeType: selectedFile.type,
            width: imageDimensions?.width,
            height: imageDimensions?.height,
          },
          retryFetch,
        );
        savedImage = result.image;
      } else {
        const result = await saveImageMetadata(
          processId!,
          {
            key: presignedData.key,
            activityId,
            size: selectedFile.size,
            mimeType: selectedFile.type,
            width: imageDimensions?.width,
            height: imageDimensions?.height,
          },
          retryFetch,
        );
        savedImage = result.image;
      }

      setUploadProgress(100);
      toast.success(t("image_uploaded", "Image uploaded successfully"));

      // Clean up preview URL since we now have the R2 URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      onComplete(savedImage);
      onOpenChange(false);
    } catch (err) {
      console.error("Image upload error:", err);
      setError(
        err instanceof Error
          ? err.message
          : t("image_upload_failed", "Failed to upload image"),
      );
      toast.error(t("image_upload_failed", "Failed to upload image"));
    } finally {
      setUploading(false);
    }
  };

  // Handle dialog close
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      clearSelection();
    }
    onOpenChange(newOpen);
  };

  // View mode - just display the existing image
  if (mode === "view" && existingImage) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              {t("view_image", "View Image")}
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center justify-center p-4">
            <img
              src={existingImage.url}
              alt={t("uploaded_image", "Uploaded image")}
              className="max-w-full max-h-[400px] object-contain rounded-lg"
            />
          </div>

          {/* Metadata */}
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            {existingImage.width && existingImage.height && (
              <span>
                {existingImage.width} x {existingImage.height}
              </span>
            )}
            <span>{formatSize(existingImage.size)}</span>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("close", "Close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            {t("upload_image", "Upload Image")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "select_image_description",
              "Select an image file to upload (JPEG, PNG, WebP, or GIF)",
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Error Alert */}
        {(error || cameraError) && (
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error || cameraError}</AlertDescription>
          </Alert>
        )}

        {/* Hidden canvas for camera capture */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Input Mode Tabs */}
        {!selectedFile && (
          <div className="flex gap-2 p-1 rounded-lg bg-muted">
            <Button
              variant={inputMode === "file" ? "default" : "ghost"}
              size="sm"
              className="flex-1"
              onClick={() => setInputMode("file")}
            >
              <FileImage className="w-4 h-4 mr-2" />
              {t("from_device", "From Device")}
            </Button>
            <Button
              variant={inputMode === "camera" ? "default" : "ghost"}
              size="sm"
              className="flex-1"
              onClick={() => setInputMode("camera")}
            >
              <Camera className="w-4 h-4 mr-2" />
              {t("from_camera", "From Camera")}
            </Button>
          </div>
        )}

        {/* File Selection Area */}
        {!selectedFile && inputMode === "file" ? (
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
              isDragging
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50",
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_EXTENSIONS}
              onChange={handleInputChange}
              className="hidden"
            />
            <FileImage className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="mb-2 text-sm text-muted-foreground">
              {t(
                "drag_drop_image",
                "Drag and drop an image here, or click to select",
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("max_size", "Maximum size")}: {formatSize(maxSize)}
            </p>
          </div>
        ) : !selectedFile && inputMode === "camera" ? (
          /* Camera Capture Area */
          <div className="space-y-4">
            <div className="relative overflow-hidden bg-black rounded-lg aspect-video">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              {/* Switch camera button */}
              <Button
                variant="secondary"
                size="icon"
                className="absolute top-2 right-2 opacity-80 hover:opacity-100"
                onClick={switchCamera}
                title={t("switch_camera", "Switch camera")}
              >
                <SwitchCamera className="w-4 h-4" />
              </Button>
            </div>
            <Button
              className="w-full"
              onClick={capturePhoto}
              disabled={!cameraStream}
            >
              <Camera className="w-4 h-4 mr-2" />
              {t("capture_photo", "Capture Photo")}
            </Button>
          </div>
        ) : (
          /* Preview Area */
          <div className="space-y-4">
            <div className="relative">
              <img
                src={previewUrl!}
                alt={t("preview", "Preview")}
                className="w-full max-h-[300px] object-contain rounded-lg bg-muted"
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2"
                onClick={clearSelection}
                disabled={uploading}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* File Info */}
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span className="truncate max-w-[200px]">
                {selectedFile?.name}
              </span>
              <div className="flex items-center gap-2">
                {imageDimensions && (
                  <span>
                    {imageDimensions.width} x {imageDimensions.height}
                  </span>
                )}
                <span>{formatSize(selectedFile?.size || 0)}</span>
              </div>
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>{t("uploading_image", "Uploading image...")}</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full transition-all bg-primary"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={uploading}
          >
            {t("cancel", "Cancel")}
          </Button>
          <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("uploading", "Uploading...")}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                {t("upload", "Upload")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
